import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { CRMUser, TimberProject, ProjectTimeEntry } from '../../types';
import { listApprovedDesignerInvoices, type DesignerInvoiceDoc } from '../../utils/designerInvoiceService';
import {
  designerInvoiceReportEntries,
  type InvoicePerson,
} from '../../utils/designerInvoiceProjectTime';
import {
  buildBillableProjectTimeReport,
  allocateUserDisplayMinutes,
  formatReportHours,
  getProjectBillingLabel,
  projectRowDisplayMinutes,
  sumDisplayReportMinutes,
  type BillableProjectReportRow,
  type ProjectBillingLabel,
} from '../../utils/billableProjectTimeReport';
import { X, Download, FileText, ChevronDown, ChevronRight } from 'lucide-react';

type ReportPeriod = 'week' | 'biweek' | 'custom';

interface ProjectReportProps {
  project?: TimberProject | null;
  projects?: TimberProject[];
  timeEntries: ProjectTimeEntry[];
  /** Used to place invoice hours on the same designer or engineer the project timer uses. */
  crmUsers?: CRMUser[];
  onClose: () => void;
}

function entryWorkDate(entry: ProjectTimeEntry): Date {
  return entry.date ?? entry.startTime ?? new Date();
}

function billingBadgeClass(label: ProjectBillingLabel): string {
  if (label === 'Billable') return 'pm-report-billing-tag pm-report-billing-tag--billable';
  if (label === 'Non-billable') return 'pm-report-billing-tag pm-report-billing-tag--nonbillable';
  return 'pm-report-billing-tag pm-report-billing-tag--contract';
}

const ProjectReport: React.FC<ProjectReportProps> = ({
  project,
  projects = [],
  timeEntries,
  crmUsers = [],
  onClose,
}) => {
  const isAllProjects = !project;
  const [period, setPeriod] = useState<ReportPeriod>('week');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [showNonBillable, setShowNonBillable] = useState(false);
  const [expandedProjectIds, setExpandedProjectIds] = useState<Set<string>>(new Set());
  const printRef = useRef<HTMLDivElement>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const dateRange = useMemo(() => {
    const now = new Date();
    let start: Date;
    let end: Date;
    if (period === 'custom' && customStart && customEnd) {
      start = new Date(`${customStart}T00:00:00`);
      end = new Date(`${customEnd}T23:59:59`);
    } else if (period === 'biweek') {
      end = new Date(now);
      start = new Date(now);
      start.setDate(start.getDate() - 14);
    } else {
      end = new Date(now);
      start = new Date(now);
      start.setDate(start.getDate() - 7);
    }
    return { start, end };
  }, [period, customStart, customEnd]);

  const [approvedInvoices, setApprovedInvoices] = useState<DesignerInvoiceDoc[]>([]);

  useEffect(() => {
    let cancelled = false;
    listApprovedDesignerInvoices()
      .then((rows) => {
        if (!cancelled) setApprovedInvoices(rows);
      })
      .catch(() => {
        if (!cancelled) setApprovedInvoices([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const invoicePeople = useMemo<InvoicePerson[]>(() => {
    const onProject = new Set(timeEntries.map((entry) => entry.userId));
    return crmUsers
      .filter(
        (user) =>
          user.isActive
          && (user.role === 'designer'
            || user.role === 'architect'
            || user.role === 'engineer'
            || onProject.has(user.uid)),
      )
      .map((user) => ({
        userId: user.uid,
        userName: user.displayName || user.email || 'User',
        company: user.company,
      }));
  }, [crmUsers, timeEntries]);

  const trackedUserIdsByProject = useMemo(() => {
    const byProject = new Map<string, Set<string>>();
    for (const entry of timeEntries) {
      if (!entry.userId || !entry.projectId) continue;
      let ids = byProject.get(entry.projectId);
      if (!ids) {
        ids = new Set();
        byProject.set(entry.projectId, ids);
      }
      ids.add(entry.userId);
    }
    return byProject;
  }, [timeEntries]);

  const filteredEntries = useMemo(() => {
    const invoiceEntries = designerInvoiceReportEntries({
      invoices: approvedInvoices,
      people: invoicePeople,
      trackedUserIdsByProject,
    });
    return [...timeEntries, ...invoiceEntries].filter((e) => {
      const d = entryWorkDate(e);
      return d >= dateRange.start && d <= dateRange.end;
    });
  }, [timeEntries, approvedInvoices, invoicePeople, trackedUserIdsByProject, dateRange]);

  const billableReport = useMemo(
    () =>
      buildBillableProjectTimeReport({
        entries: filteredEntries,
        projects: isAllProjects ? projects : project ? [project] : [],
        showNonBillable: isAllProjects ? showNonBillable : true,
        scopeProjectId: isAllProjects ? undefined : project?.id,
      }),
    [filteredEntries, projects, project, isAllProjects, showNonBillable],
  );

  const formatDate = (d: Date) =>
    new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const reportTitle = isAllProjects ? 'All Projects Time Report' : project!.name;
  const reportSubtitle = isAllProjects
    ? `${formatDate(dateRange.start)} – ${formatDate(dateRange.end)}`
    : `${project!.clientName} · ${project!.location || '—'} · ${formatDate(dateRange.start)} – ${formatDate(dateRange.end)}`;

  const singleProjectBilling = !isAllProjects && project ? getProjectBillingLabel(project) : null;
  const singleProjectUsers = billableReport.rows[0]
    ? allocateUserDisplayMinutes(billableReport.rows[0])
    : [];

  const displayTotalMinutes = useMemo(
    () => sumDisplayReportMinutes(billableReport.rows.map((r) => r.minutes)),
    [billableReport.rows],
  );

  const displayNonBillableMinutes = useMemo(
    () =>
      sumDisplayReportMinutes(
        billableReport.rows.filter((r) => !r.isBillable).map((r) => r.minutes),
      ),
    [billableReport.rows],
  );

  const toggleProjectExpanded = useCallback((projectId: string) => {
    setExpandedProjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  }, []);

  const renderBillingBadge = (label: ProjectBillingLabel) => (
    <span className={`phase-badge ${billingBadgeClass(label)}`}>{label}</span>
  );

  const renderUserSubtable = (row: BillableProjectReportRow) => {
    const users = allocateUserDisplayMinutes(row);
    const projectTotal = projectRowDisplayMinutes(row);
    if (users.length === 0) {
      return <p className="pm-report-user-panel-empty">No billable time by team member in this period.</p>;
    }
    return (
      <div className="pm-report-user-panel">
        <div className="pm-report-user-panel-head">
          <span>Team member</span>
          <span className="pm-report-user-panel-hours">Hours</span>
        </div>
        {users.map((u) => (
          <div key={u.userId} className="pm-report-user-panel-row">
            <span className="pm-report-user-panel-name">{u.userName}</span>
            <span className="pm-report-user-panel-hours">{formatReportHours(u.displayMinutes)}</span>
          </div>
        ))}
        <div className="pm-report-user-panel-row pm-report-user-panel-total">
          <span>Project total</span>
          <span className="pm-report-user-panel-hours">{formatReportHours(projectTotal)}</span>
        </div>
      </div>
    );
  };

  const handleDownloadPDF = async () => {
    setGeneratingPdf(true);
    try {
      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
      const W = doc.internal.pageSize.getWidth();
      const M = 14;
      const BRAND = {
        dark: [44, 62, 47] as [number, number, number],
        muted: [148, 163, 184] as [number, number, number],
        text: [30, 30, 30] as [number, number, number],
        billable: [13, 148, 136] as [number, number, number],
      };

      const drawHeader = () => {
        doc.setFillColor(...BRAND.dark);
        doc.rect(0, 0, W, 18, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(reportTitle, M, 12);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(reportSubtitle, W - M, 12, { align: 'right' });
      };

      drawHeader();
      let y = 24;

      const summaryItems: [string, string][] = [
        ['Total Hours', formatReportHours(displayTotalMinutes)],
      ];
      if (isAllProjects) {
        summaryItems.push(['Billable Projects', String(billableReport.billableProjectCount)]);
        if (showNonBillable && displayNonBillableMinutes > 0) {
          summaryItems.push(['Non-billable Hours', formatReportHours(displayNonBillableMinutes)]);
        }
      } else if (singleProjectBilling) {
        summaryItems.push(['Billing', singleProjectBilling]);
      }

      const cardW = Math.min(52, (W - M * 2 - (summaryItems.length - 1) * 3) / summaryItems.length);
      summaryItems.forEach(([label, value], i) => {
        const cx = M + i * (cardW + 3);
        doc.setFillColor(245, 245, 243);
        doc.roundedRect(cx, y, cardW, 16, 2, 2, 'F');
        doc.setTextColor(...BRAND.dark);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(value, cx + cardW / 2, y + 8, { align: 'center' });
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...BRAND.muted);
        doc.text(label, cx + cardW / 2, y + 13.5, { align: 'center' });
      });
      y += 22;

      if (isAllProjects) {
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...BRAND.muted);
        const note = showNonBillable
          ? 'Includes billable and non-billable precon projects plus contracted projects with time in this period.'
          : 'Billable precon projects only (set Billing in Project Hub). Non-billable projects hidden.';
        const lines = doc.splitTextToSize(note, W - M * 2);
        doc.text(lines, M, y);
        y += lines.length * 3.2 + 4;
      }

      const sectionTitle = isAllProjects ? 'Billable Hours by Project' : 'Hours by Team Member';
      doc.setTextColor(...BRAND.dark);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(sectionTitle, M, y);
      y += 2;

      if (isAllProjects) {
        const head = [['Project', 'Client', 'Billing', 'Hours']];
        const body: (string | { content: string; colSpan?: number; styles?: object })[][] = [];

        for (const row of billableReport.rows) {
          const users = allocateUserDisplayMinutes(row);
          const projectTotal = projectRowDisplayMinutes(row);
          body.push([
            row.projectName,
            row.clientName,
            row.billingLabel,
            formatReportHours(projectTotal),
          ]);
          for (const u of users) {
            body.push([
              { content: `    ${u.userName}`, styles: { fontStyle: 'italic', textColor: BRAND.muted } } as any,
              '',
              '',
              formatReportHours(u.displayMinutes),
            ]);
          }
          if (users.length > 0) {
            body.push([
              { content: '  Project subtotal', colSpan: 3, styles: { fontStyle: 'bold', fontSize: 7 } } as any,
              { content: formatReportHours(projectTotal), styles: { fontStyle: 'bold' } } as any,
            ]);
          }
        }
        body.push([
          { content: 'Total', colSpan: 3, styles: { fontStyle: 'bold' } } as any,
          formatReportHours(displayTotalMinutes),
        ]);

        autoTable(doc, {
          startY: y,
          head,
          body,
          margin: { left: M, right: M },
          styles: { fontSize: 8, cellPadding: 2.5, textColor: BRAND.text },
          headStyles: { fillColor: BRAND.dark, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7 },
          columnStyles: { 3: { halign: 'right', fontStyle: 'bold' } },
        });
      } else {
        const row = billableReport.rows[0];
        const userRows = row ? allocateUserDisplayMinutes(row) : [];
        autoTable(doc, {
          startY: y,
          head: [['Name', 'Hours']],
          body: [
            ...userRows.map((u) => [u.userName, formatReportHours(u.displayMinutes)]),
            [{ content: 'Total', styles: { fontStyle: 'bold' } } as any, formatReportHours(displayTotalMinutes)],
          ],
          margin: { left: M, right: M },
          styles: { fontSize: 8, cellPadding: 2.5, textColor: BRAND.text },
          headStyles: { fillColor: BRAND.dark, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7 },
          columnStyles: { 1: { halign: 'right', fontStyle: 'bold' } },
        });
      }

      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...BRAND.muted);
        doc.text(`Page ${i} of ${totalPages}`, W - M, doc.internal.pageSize.getHeight() - 8, { align: 'right' });
        doc.text(new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }), M, doc.internal.pageSize.getHeight() - 8);
      }

      const filename = isAllProjects
        ? `Billable_Time_Report_All_Projects_${formatDate(dateRange.start).replace(/\s/g, '_')}.pdf`
        : `Billable_Time_Report_${project!.name.replace(/\s+/g, '_')}_${formatDate(dateRange.start).replace(/\s/g, '_')}.pdf`;
      doc.save(filename);
    } catch (err) {
      console.error('Failed to generate PDF:', err);
    } finally {
      setGeneratingPdf(false);
    }
  };

  return (
    <div className="pm-report-overlay" onClick={onClose}>
      <div className="pm-report-panel" onClick={(e) => e.stopPropagation()}>
        <div className="pm-report-header">
          <h3>
            <FileText size={18} /> {isAllProjects ? 'All Projects Report' : 'Project Report'}
          </h3>
          <div className="pm-report-header-actions">
            <button className="pm-btn pm-btn-primary pm-btn-sm" onClick={handleDownloadPDF} disabled={generatingPdf}>
              <Download size={13} /> {generatingPdf ? 'Generating...' : 'Download PDF'}
            </button>
            <button className="pm-btn-ghost pm-btn-sm" onClick={onClose}>
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="pm-report-period">
          {(['week', 'biweek', 'custom'] as ReportPeriod[]).map((p) => (
            <button
              key={p}
              className={`pm-report-period-btn ${period === p ? 'active' : ''}`}
              onClick={() => setPeriod(p)}
            >
              {p === 'week' ? 'This Week' : p === 'biweek' ? 'Bi-Weekly' : 'Custom'}
            </button>
          ))}
          {period === 'custom' && (
            <div className="pm-report-custom-dates">
              <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="pm-inline-input" />
              <span>to</span>
              <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="pm-inline-input" />
            </div>
          )}
          {isAllProjects && (
            <label className="pm-report-billable-toggle">
              <input
                type="checkbox"
                checked={showNonBillable}
                onChange={(e) => setShowNonBillable(e.target.checked)}
              />
              Show non-billable projects
            </label>
          )}
        </div>

        <div className="pm-report-body pm-scroll" ref={printRef}>
          <h1>{reportTitle}</h1>
          <div className="meta">{reportSubtitle}</div>
          {isAllProjects && (
            <p className="meta pm-report-billable-note">
              {showNonBillable
                ? 'Showing all projects with CRM time or approved design and engineering invoice hours in this period.'
                : 'Showing billable precon projects only. Toggle above to include non-billable and contracted work. Invoice hours are included.'}
            </p>
          )}

          <div className="summary-grid pm-report-summary-compact">
            <div className="summary-card">
              <div className="num">{formatReportHours(displayTotalMinutes)}</div>
              <div className="label">Total Hours</div>
            </div>
            {isAllProjects ? (
              <div className="summary-card">
                <div className="num" style={{ color: '#0d9488' }}>
                  {billableReport.billableProjectCount}
                </div>
                <div className="label">Billable Projects</div>
              </div>
            ) : (
              singleProjectBilling && (
                <div className="summary-card">
                  <div className="num pm-report-billing-summary">{renderBillingBadge(singleProjectBilling)}</div>
                  <div className="label">Billing</div>
                </div>
              )
            )}
            {isAllProjects && showNonBillable && displayNonBillableMinutes > 0 && (
              <div className="summary-card">
                <div className="num">{formatReportHours(displayNonBillableMinutes)}</div>
                <div className="label">Non-billable Hours</div>
              </div>
            )}
          </div>

          {isAllProjects ? (
            <>
              <h2>Billable Hours by Project</h2>
              {billableReport.rows.length === 0 ? (
                <p className="pm-report-empty">
                  No billable project time in this period.
                  {!showNonBillable && ' Enable “Show non-billable projects” to see other work.'}
                </p>
              ) : (
                <table className="pm-report-project-table">
                  <thead>
                    <tr>
                      <th aria-label="Expand" />
                      <th>Project</th>
                      <th>Client</th>
                      <th>Billing</th>
                      <th className="hours-col">Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billableReport.rows.map((row) => {
                      const expanded = expandedProjectIds.has(row.projectId);
                      return (
                        <React.Fragment key={row.projectId}>
                          <tr
                            className={`pm-report-project-row ${expanded ? 'pm-report-project-row--expanded' : ''}`}
                            onClick={() => toggleProjectExpanded(row.projectId)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleProjectExpanded(row.projectId);
                              }
                            }}
                          >
                            <td className="pm-report-expand-cell">
                              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </td>
                            <td>{row.projectName}</td>
                            <td>{row.clientName}</td>
                            <td>{renderBillingBadge(row.billingLabel)}</td>
                            <td className="hours-col">{formatReportHours(projectRowDisplayMinutes(row))}</td>
                          </tr>
                          {expanded && (
                            <tr className="pm-report-project-detail-row">
                              <td colSpan={5}>{renderUserSubtable(row)}</td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    <tr className="pm-report-project-total-row">
                      <td colSpan={4}>Total</td>
                      <td className="hours-col">{formatReportHours(displayTotalMinutes)}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </>
          ) : (
            <>
              <h2>Hours by Team Member</h2>
              {singleProjectUsers.length === 0 ? (
                <p className="pm-report-empty">No time logged on this project in the selected period.</p>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th className="hours-col">Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {singleProjectUsers.map((u) => (
                      <tr key={u.userId}>
                        <td>{u.userName}</td>
                        <td className="hours-col">{formatReportHours(u.displayMinutes)}</td>
                      </tr>
                    ))}
                    <tr className="pm-report-project-total-row">
                      <td>Total</td>
                      <td className="hours-col">{formatReportHours(displayTotalMinutes)}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectReport;
